import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE, { withApiCredentials } from "../../api";
import { trackProductEvent } from "../../analytics/trackEvent";
import {
  buildGroupsSearch,
  getGroupsStateFromSearch,
} from "../../app/navigationState";
import useLocationStateStack from "../../app/useLocationStateStack";
import {
  buildTypeOfFishingFilterOptions,
  getTypeOfFishingOptionsForWay,
} from "../../utils/catchMetadataOptions";
import {
  getEditableGroupMetadataValue,
  readErrorDetail,
} from "./GroupsSharedUi";
import {
  buildFilteredGroups,
  findGroupById,
  findUserById,
} from "./groupsPageSelectors";
import useGroupCreateFormState from "./useGroupCreateFormState";
import useGroupMembershipAdminState from "./useGroupMembershipAdminState";
import useGroupPostsState from "./useGroupPostsState";

export default function useGroupsPageState({
  user,
  users = [],
  openGroupRequest,
  onOpenUserProfile,
  onGroupsLoaded,
  t,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupFilterScope, setGroupFilterScope] = useState("all");
  const [groupFilterWaterType, setGroupFilterWaterType] = useState("all");
  const [groupFilterWayOfFishing, setGroupFilterWayOfFishing] = useState("all");
  const [groupFilterTypeOfFishing, setGroupFilterTypeOfFishing] = useState("all");
  const [fishingMethods, setFishingMethods] = useState({});
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [activeGroupHomeId, setActiveGroupHomeId] = useState(null);
  const [fullCoverGroupId, setFullCoverGroupId] = useState(null);
  const [groupCoverUpdateTargetId, setGroupCoverUpdateTargetId] = useState(null);
  const [groupCoverUpdateFile, setGroupCoverUpdateFile] = useState(null);
  const [groupCoverUpdatePreviewUrl, setGroupCoverUpdatePreviewUrl] = useState("");
  const [groupCoverUpdatePosition, setGroupCoverUpdatePosition] = useState({
    x: 50,
    y: 50,
    scale: 1,
  });
  const [groupCoverUpdateLoading, setGroupCoverUpdateLoading] = useState(false);
  const [groupCoverUpdateError, setGroupCoverUpdateError] = useState("");
  const [groupMetadataEditTargetId, setGroupMetadataEditTargetId] =
    useState(null);
  const [groupMetadataEditWaterType, setGroupMetadataEditWaterType] =
    useState("all");
  const [groupMetadataEditWayOfFishing, setGroupMetadataEditWayOfFishing] =
    useState("all");
  const [groupMetadataEditTypeOfFishing, setGroupMetadataEditTypeOfFishing] =
    useState("all");
  const [groupMetadataEditRules, setGroupMetadataEditRules] = useState("");
  const [
    groupMetadataEditPostingApprovalMode,
    setGroupMetadataEditPostingApprovalMode,
  ] = useState("member_posts_auto_publish");
  const [groupMetadataEditLoading, setGroupMetadataEditLoading] =
    useState(false);
  const [groupMetadataEditError, setGroupMetadataEditError] = useState("");
  const handledOpenRequestRef = useRef(null);
  const openGroupFetchRequestRef = useRef(null);
  const groupCoverUpdateInputRef = useRef(null);

  const token =
    typeof window !== "undefined" ? window.localStorage.getItem("token") : "";
  const userId = user?.id || "";

  const selectedGroup = findGroupById(groups, selectedGroupId);
  const activeGroupHome = findGroupById(groups, activeGroupHomeId);

  const {
    prepareOpenGroupHome,
    resetGroupHomeState,
    ...groupPostsController
  } = useGroupPostsState({
    activeGroupHome,
    activeGroupHomeId,
    groups,
    token,
    user,
    userId,
    t,
  });

  const groupFilterTypeOfFishingOptions = useMemo(() => {
    if (groupFilterWayOfFishing === "all") {
      return buildTypeOfFishingFilterOptions(fishingMethods).map(
        (option) => option.label
      );
    }

    return getTypeOfFishingOptionsForWay(fishingMethods, groupFilterWayOfFishing);
  }, [fishingMethods, groupFilterWayOfFishing]);

  const groupMetadataEditTypeOfFishingOptions = useMemo(() => {
    if (groupMetadataEditWayOfFishing === "all") {
      return buildTypeOfFishingFilterOptions(fishingMethods).map(
        (option) => option.label
      );
    }

    return getTypeOfFishingOptionsForWay(
      fishingMethods,
      groupMetadataEditWayOfFishing
    );
  }, [fishingMethods, groupMetadataEditWayOfFishing]);

  const resetGroupCoverUpdateState = useCallback(() => {
    setGroupCoverUpdateTargetId(null);
    setGroupCoverUpdateFile(null);
    setGroupCoverUpdatePreviewUrl("");
    setGroupCoverUpdatePosition({ x: 50, y: 50 });
    setGroupCoverUpdateLoading(false);
    setGroupCoverUpdateError("");
    if (groupCoverUpdateInputRef.current) {
      groupCoverUpdateInputRef.current.value = "";
    }
  }, []);

  const resetGroupMetadataEditState = useCallback(() => {
    setGroupMetadataEditTargetId(null);
    setGroupMetadataEditWaterType("all");
    setGroupMetadataEditWayOfFishing("all");
    setGroupMetadataEditTypeOfFishing("all");
    setGroupMetadataEditRules("");
    setGroupMetadataEditPostingApprovalMode("member_posts_auto_publish");
    setGroupMetadataEditLoading(false);
    setGroupMetadataEditError("");
  }, []);

  const closeGroupCoverViewer = useCallback(() => {
    setFullCoverGroupId(null);
  }, []);

  const openGroupCoverUpdatePicker = useCallback(() => {
    setGroupCoverUpdateError("");
    groupCoverUpdateInputRef.current?.click();
  }, []);

  const upsertGroup = useCallback((updatedGroup, viewerId) => {
    void viewerId;
    setGroups((prevGroups) => {
      const nextGroups = [...prevGroups];
      const groupIndex = nextGroups.findIndex(
        (group) => group.id === updatedGroup.id
      );

      if (groupIndex === -1) {
        return [updatedGroup, ...nextGroups];
      }

      nextGroups[groupIndex] = updatedGroup;
      return nextGroups;
    });
  }, []);

  const {
    peopleListGroupId,
    peopleListType,
    resetPeopleListState,
    ...membershipAdminController
  } = useGroupMembershipAdminState({
    groups,
    allUsers,
    user,
    token,
    t,
    selectedGroupId,
    activeGroupHomeId,
    setGroups,
    setSelectedGroupId,
    setActiveGroupHomeId,
    resetGroupHomeState,
    upsertGroup,
  });
  const {
    resetInviteState,
    closeConfirmModal,
    closePeopleList,
    isAdmin,
    inviteGroupId,
    groupInvitePanelRef,
  } = membershipAdminController;

  const groupsRouteState = useMemo(
    () => getGroupsStateFromSearch(location.search),
    [location.search]
  );

  const createFormState = useGroupCreateFormState({
    fishingMethods,
    isCreateOpen: groupsRouteState.view === "create",
    token,
    user,
    t,
    onCreateSuccess: (createdGroup) => {
      setGroups((prevGroups) => [createdGroup, ...prevGroups]);
      resetInviteState(null);
      navigate(
        {
          pathname: "/",
          search: buildGroupsSearch(location.search, {
            view: "detail",
            groupId: createdGroup.id,
          }),
          hash: "",
        },
        {
          state: location.state,
        }
      );
    },
  });
  const {
    openCreateForm: prepareCreateForm,
    closeCreateForm: dismissCreateForm,
    cancelCreateGroup: resetCreateForm,
  } = createFormState;

  const groupsOverlayHistoryItems = useMemo(
    () => [
      {
        id: "groups-people-list",
        isOpen: Boolean(membershipAdminController.peopleListGroup),
        close: closePeopleList,
      },
      {
        id: "groups-cover-viewer",
        isOpen: Boolean(fullCoverGroupId),
        close: closeGroupCoverViewer,
      },
      {
        id: "groups-cover-update",
        isOpen: Boolean(groupCoverUpdateTargetId && groupCoverUpdatePreviewUrl),
        close: resetGroupCoverUpdateState,
      },
      {
        id: "groups-metadata-edit",
        isOpen: Boolean(groupMetadataEditTargetId),
        close: resetGroupMetadataEditState,
      },
      {
        id: "groups-confirm",
        isOpen: Boolean(membershipAdminController.confirmAction),
        close: () => closeConfirmModal(true),
      },
    ],
    [
      closeConfirmModal,
      closeGroupCoverViewer,
      closePeopleList,
      fullCoverGroupId,
      groupCoverUpdatePreviewUrl,
      groupCoverUpdateTargetId,
      groupMetadataEditTargetId,
      membershipAdminController.confirmAction,
      membershipAdminController.peopleListGroup,
      resetGroupCoverUpdateState,
      resetGroupMetadataEditState,
    ]
  );

  const { requestReplaceSync: requestGroupsOverlayReplace } =
    useLocationStateStack("groupsOverlayStack", groupsOverlayHistoryItems);

  const navigateToGroupsView = useCallback(
    (view, groupId = "", options = {}) => {
      const normalizedView =
        view === "create" || view === "detail" || view === "home"
          ? view
          : "list";
      const normalizedGroupId =
        normalizedView === "detail" || normalizedView === "home"
          ? String(groupId || "").trim()
          : "";
      const nextSearch = buildGroupsSearch(location.search, { view, groupId });

      if (
        location.pathname === "/" &&
        groupsRouteState.view === normalizedView &&
        groupsRouteState.groupId === normalizedGroupId &&
        !options.force
      ) {
        return;
      }

      if (
        location.pathname === "/" &&
        location.search === nextSearch &&
        !options.force
      ) {
        return;
      }

      navigate(
        {
          pathname: "/",
          search: nextSearch,
          hash: "",
        },
        {
          replace: Boolean(options.replace),
          state: location.state,
        }
      );
    },
    [
      groupsRouteState.groupId,
      groupsRouteState.view,
      location.pathname,
      location.search,
      location.state,
      navigate,
    ]
  );

  const openGroup = useCallback(
    (groupId, options = {}) => {
      trackProductEvent("group_opened", {
        featureArea: "groups",
        metadata: {
          source: options.showInvite ? "invite_entry" : "groups_discovery",
          view: "detail",
        },
      });
      setSelectedGroupId(groupId);
      setActiveGroupHomeId(null);
      if (
        membershipAdminController.peopleListGroup ||
        fullCoverGroupId ||
        groupCoverUpdateTargetId ||
        groupMetadataEditTargetId ||
        membershipAdminController.confirmAction
      ) {
        requestGroupsOverlayReplace();
      }
      resetPeopleListState();
      resetInviteState(options.showInvite ? groupId : null);
      closeConfirmModal(true);
      resetGroupHomeState();
      navigateToGroupsView("detail", groupId);
    },
    [
      closeConfirmModal,
      fullCoverGroupId,
      groupCoverUpdateTargetId,
      groupMetadataEditTargetId,
      membershipAdminController.confirmAction,
      membershipAdminController.peopleListGroup,
      navigateToGroupsView,
      requestGroupsOverlayReplace,
      resetGroupHomeState,
      resetInviteState,
      resetPeopleListState,
    ]
  );

  const closeGroupHome = useCallback(() => {
    setActiveGroupHomeId(null);
    if (
      membershipAdminController.peopleListGroup ||
      fullCoverGroupId ||
      groupCoverUpdateTargetId ||
      groupMetadataEditTargetId ||
      membershipAdminController.confirmAction
    ) {
      requestGroupsOverlayReplace();
    }
    resetPeopleListState();
    resetInviteState(null);
    closeConfirmModal(true);
    resetGroupHomeState();
    navigateToGroupsView("list");
  }, [
    closeConfirmModal,
    fullCoverGroupId,
    groupCoverUpdateTargetId,
    groupMetadataEditTargetId,
    membershipAdminController.confirmAction,
    membershipAdminController.peopleListGroup,
    navigateToGroupsView,
    requestGroupsOverlayReplace,
    resetGroupHomeState,
    resetInviteState,
    resetPeopleListState,
  ]);

  const openGroupHome = useCallback(
    (groupId) => {
      trackProductEvent("group_opened", {
        featureArea: "groups",
        metadata: { source: "groups_home", view: "home" },
      });
      setActiveGroupHomeId(groupId);
      setSelectedGroupId(null);
      if (
        membershipAdminController.peopleListGroup ||
        fullCoverGroupId ||
        groupCoverUpdateTargetId ||
        groupMetadataEditTargetId ||
        membershipAdminController.confirmAction
      ) {
        requestGroupsOverlayReplace();
      }
      resetPeopleListState();
      resetInviteState(null);
      closeConfirmModal(true);
      prepareOpenGroupHome();
      navigateToGroupsView("home", groupId);
    },
    [
      closeConfirmModal,
      fullCoverGroupId,
      groupCoverUpdateTargetId,
      groupMetadataEditTargetId,
      membershipAdminController.confirmAction,
      membershipAdminController.peopleListGroup,
      navigateToGroupsView,
      prepareOpenGroupHome,
      requestGroupsOverlayReplace,
      resetInviteState,
      resetPeopleListState,
    ]
  );

  function closeGroupDetail() {
    setSelectedGroupId(null);
    if (
      membershipAdminController.peopleListGroup ||
      fullCoverGroupId ||
      groupCoverUpdateTargetId ||
      groupMetadataEditTargetId ||
      membershipAdminController.confirmAction
    ) {
      requestGroupsOverlayReplace();
    }
    resetPeopleListState();
    resetInviteState(null);
    closeConfirmModal(true);
    navigateToGroupsView("list");
  }

  useEffect(() => {
    const nextSelectedGroupId =
      groupsRouteState.view === "detail" ? groupsRouteState.groupId || null : null;
    const nextActiveGroupHomeId =
      groupsRouteState.view === "home" ? groupsRouteState.groupId || null : null;
    setSelectedGroupId((currentValue) =>
      currentValue === nextSelectedGroupId ? currentValue : nextSelectedGroupId
    );
    setActiveGroupHomeId((currentValue) =>
      currentValue === nextActiveGroupHomeId ? currentValue : nextActiveGroupHomeId
    );
  }, [groupsRouteState.groupId, groupsRouteState.view]);

  useEffect(() => {
    if (groupsRouteState.view !== "home" && activeGroupHomeId) {
      resetGroupHomeState();
    }

    if (groupsRouteState.view !== "detail" && groupsRouteState.view !== "home") {
      resetInviteState(null);
    }
  }, [
    activeGroupHomeId,
    groupsRouteState.view,
    resetGroupHomeState,
    resetInviteState,
  ]);

  const loadGroups = useCallback(async () => {
    if (!userId) {
      setGroups([]);
      if (typeof onGroupsLoaded === "function") {
        onGroupsLoaded([]);
      }
      setLoading(false);
      setError("");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const queryParams = new URLSearchParams();
      if (groupFilterWaterType !== "all") {
        queryParams.set("waterType", groupFilterWaterType);
      }
      if (groupFilterWayOfFishing !== "all") {
        queryParams.set("wayOfFishing", groupFilterWayOfFishing);
      }
      if (groupFilterTypeOfFishing !== "all") {
        queryParams.set("typeOfFishing", groupFilterTypeOfFishing);
      }

      const queryString = queryParams.toString();
      const groupsUrl = queryString
        ? `${API_BASE}/groups/?${queryString}`
        : `${API_BASE}/groups/`;
      const response = await fetch(
        groupsUrl,
        withApiCredentials({
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, t("groups_load_failed")));
      }

      const nextGroups = Array.isArray(data) ? data : [];
      setGroups(nextGroups);
      if (!queryString && typeof onGroupsLoaded === "function") {
        onGroupsLoaded(nextGroups);
      }
    } catch (err) {
      setError(err.message || t("groups_load_failed"));
    } finally {
      setLoading(false);
    }
  }, [
    groupFilterTypeOfFishing,
    groupFilterWaterType,
    groupFilterWayOfFishing,
    onGroupsLoaded,
    t,
    token,
    userId,
  ]);

  const fetchGroupById = useCallback(
    async (groupId) => {
      if (!groupId) {
        throw new Error(t("groups_load_failed"));
      }

      const response = await fetch(
        `${API_BASE}/groups/${groupId}`,
        withApiCredentials({
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, t("groups_load_failed")));
      }

      return data;
    },
    [t, token]
  );

  const loadAllUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/users`);
      const data = await response.json().catch(() => []);
      if (!response.ok || !Array.isArray(data)) {
        throw new Error("Could not load users.");
      }
      setAllUsers(data);
    } catch {
      setAllUsers([]);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    let isMounted = true;

    fetch(`${process.env.PUBLIC_URL}/JSON/fishingMethods.json`)
      .then((response) => response.json())
      .then((data) => {
        if (isMounted && data && typeof data === "object") {
          setFishingMethods(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setFishingMethods({});
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setAllUsers([]);
      return;
    }

    if (Array.isArray(users) && users.length > 0) {
      setAllUsers(users);
      return;
    }

    loadAllUsers();
  }, [loadAllUsers, userId, users]);

  useEffect(() => {
    const handleGroupsUpdated = () => {
      loadGroups();

      if (Array.isArray(users) && users.length > 0) {
        setAllUsers(users);
        return;
      }

      loadAllUsers();
    };

    window.addEventListener("groupsUpdated", handleGroupsUpdated);
    return () =>
      window.removeEventListener("groupsUpdated", handleGroupsUpdated);
  }, [loadAllUsers, loadGroups, users]);

  useEffect(() => {
    if (!selectedGroupId || loading || groupsRouteState.view !== "detail") {
      return;
    }
    const selectedGroupStillExists = groups.some(
      (group) => group.id === selectedGroupId
    );
    if (!selectedGroupStillExists) {
      requestGroupsOverlayReplace();
      setSelectedGroupId(null);
      resetInviteState(null);
      navigateToGroupsView("list", "", { replace: true });
    }
  }, [
    groups,
    navigateToGroupsView,
    requestGroupsOverlayReplace,
    resetInviteState,
    selectedGroupId,
    loading,
    groupsRouteState.view,
  ]);

  useEffect(() => {
    if (!activeGroupHomeId || loading || groupsRouteState.view !== "home") {
      return;
    }

    const activeGroupStillAccessible = groups.some(
      (group) =>
        group.id === activeGroupHomeId && group.members.includes(user?.id)
    );

    if (!activeGroupStillAccessible) {
      requestGroupsOverlayReplace();
      closeGroupHome();
    }
  }, [
    activeGroupHomeId,
    closeGroupHome,
    groups,
    requestGroupsOverlayReplace,
    user?.id,
    loading,
    groupsRouteState.view,
  ]);

  useEffect(() => {
    if (!peopleListGroupId) return;

    const peopleGroupStillVisible = groups.some(
      (group) => group.id === peopleListGroupId
    );

    if (!peopleGroupStillVisible) {
      resetPeopleListState();
    }
  }, [groups, peopleListGroupId, resetPeopleListState]);

  useEffect(() => {
    if (
      fullCoverGroupId &&
      !groups.some((group) => group.id === fullCoverGroupId)
    ) {
      requestGroupsOverlayReplace();
      setFullCoverGroupId(null);
    }

    if (
      groupCoverUpdateTargetId &&
      !groups.some((group) => group.id === groupCoverUpdateTargetId)
    ) {
      requestGroupsOverlayReplace();
      resetGroupCoverUpdateState();
    }
  }, [
    fullCoverGroupId,
    groupCoverUpdateTargetId,
    groups,
    requestGroupsOverlayReplace,
    resetGroupCoverUpdateState,
  ]);

  useEffect(() => {
    if (
      groupMetadataEditTargetId &&
      !groups.some((group) => group.id === groupMetadataEditTargetId)
    ) {
      requestGroupsOverlayReplace();
      resetGroupMetadataEditState();
    }
  }, [
    groupMetadataEditTargetId,
    groups,
    requestGroupsOverlayReplace,
    resetGroupMetadataEditState,
  ]);

  useEffect(() => {
    if (!groupCoverUpdateFile) {
      setGroupCoverUpdatePreviewUrl("");
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(groupCoverUpdateFile);
    setGroupCoverUpdatePreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [groupCoverUpdateFile]);

  useEffect(() => {
    const requestKey = openGroupRequest?.nonce;
    if (!requestKey || handledOpenRequestRef.current === requestKey) {
      return;
    }

    const requestedGroupId = String(openGroupRequest?.groupId || "").trim();
    if (!requestedGroupId) {
      handledOpenRequestRef.current = requestKey;
      return;
    }

    const requestedGroup = groups.find(
      (group) => group.id === requestedGroupId
    );

    if (requestedGroup) {
      openGroup(requestedGroup.id);
      handledOpenRequestRef.current = requestKey;
      return;
    }

    if (openGroupFetchRequestRef.current === requestKey) {
      return;
    }

    openGroupFetchRequestRef.current = requestKey;
    let isActive = true;

    (async () => {
      try {
        const fetchedGroup = await fetchGroupById(requestedGroupId);
        if (!isActive || handledOpenRequestRef.current === requestKey) {
          return;
        }

        upsertGroup(fetchedGroup, userId);
        openGroup(requestedGroupId);
        handledOpenRequestRef.current = requestKey;
      } catch (err) {
        if (!isActive) {
          return;
        }

        setError(err.message || t("groups_load_failed"));
        handledOpenRequestRef.current = requestKey;
      } finally {
        if (openGroupFetchRequestRef.current === requestKey) {
          openGroupFetchRequestRef.current = null;
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [fetchGroupById, groups, openGroup, openGroupRequest, t, upsertGroup, userId]);

  useEffect(() => {
    if (!activeGroupHomeId || inviteGroupId !== activeGroupHomeId) return;

    groupInvitePanelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [activeGroupHomeId, groupInvitePanelRef, inviteGroupId]);

  const openCreateForm = useCallback(() => {
    trackProductEvent("group_create_opened", {
      featureArea: "groups",
      metadata: { source: "groups_discovery" },
    });
    if (
      membershipAdminController.peopleListGroup ||
      fullCoverGroupId ||
      groupCoverUpdateTargetId ||
      groupMetadataEditTargetId ||
      membershipAdminController.confirmAction
    ) {
      requestGroupsOverlayReplace();
    }

    setSelectedGroupId(null);
    setActiveGroupHomeId(null);
    resetPeopleListState();
    resetInviteState(null);
    closeConfirmModal(true);
    resetGroupHomeState();
    prepareCreateForm();
    navigateToGroupsView("create");
  }, [
    closeConfirmModal,
    fullCoverGroupId,
    groupCoverUpdateTargetId,
    groupMetadataEditTargetId,
    membershipAdminController.confirmAction,
    membershipAdminController.peopleListGroup,
    navigateToGroupsView,
    prepareCreateForm,
    requestGroupsOverlayReplace,
    resetGroupHomeState,
    resetInviteState,
    resetPeopleListState,
  ]);

  const closeCreateForm = useCallback(() => {
    if (
      membershipAdminController.peopleListGroup ||
      fullCoverGroupId ||
      groupCoverUpdateTargetId ||
      groupMetadataEditTargetId ||
      membershipAdminController.confirmAction
    ) {
      requestGroupsOverlayReplace();
    }

    resetPeopleListState();
    resetInviteState(null);
    closeConfirmModal(true);
    dismissCreateForm();
    navigateToGroupsView("list");
  }, [
    closeConfirmModal,
    dismissCreateForm,
    fullCoverGroupId,
    groupCoverUpdateTargetId,
    groupMetadataEditTargetId,
    membershipAdminController.confirmAction,
    membershipAdminController.peopleListGroup,
    navigateToGroupsView,
    requestGroupsOverlayReplace,
    resetInviteState,
    resetPeopleListState,
  ]);

  const cancelCreateGroup = useCallback(() => {
    if (
      membershipAdminController.peopleListGroup ||
      fullCoverGroupId ||
      groupCoverUpdateTargetId ||
      groupMetadataEditTargetId ||
      membershipAdminController.confirmAction
    ) {
      requestGroupsOverlayReplace();
    }

    resetPeopleListState();
    resetInviteState(null);
    closeConfirmModal(true);
    resetCreateForm();
    navigateToGroupsView("list");
  }, [
    closeConfirmModal,
    fullCoverGroupId,
    groupCoverUpdateTargetId,
    groupMetadataEditTargetId,
    membershipAdminController.confirmAction,
    membershipAdminController.peopleListGroup,
    navigateToGroupsView,
    requestGroupsOverlayReplace,
    resetCreateForm,
    resetInviteState,
    resetPeopleListState,
  ]);

  const activeGroupRequiresPostApproval = Boolean(
    activeGroupHome &&
      activeGroupHome.postingApprovalMode === "member_posts_need_approval" &&
      !isAdmin(activeGroupHome)
  );
  const fullCoverGroup = findGroupById(groups, fullCoverGroupId);
  const groupCoverUpdateTarget = findGroupById(groups, groupCoverUpdateTargetId);
  const groupMetadataEditTarget = findGroupById(groups, groupMetadataEditTargetId);

  const filteredGroups = useMemo(
    () =>
      buildFilteredGroups({
        groups,
        groupFilterScope,
        groupFilterWaterType,
        groupFilterWayOfFishing,
        groupFilterTypeOfFishing,
        userId: user?.id,
      }),
    [
      groupFilterScope,
      groupFilterTypeOfFishing,
      groupFilterWaterType,
      groupFilterWayOfFishing,
      groups,
      user?.id,
    ]
  );

  function getUserById(nextUserId) {
    return findUserById(allUsers, nextUserId);
  }

  function handleOpenMemberProfile(member) {
    if (!member?.username || !onOpenUserProfile) return;
    closePeopleList();
    onOpenUserProfile(member.username);
  }

  function canOpenGroupCover(group) {
    return Boolean(group?.coverImage && user && group.members.includes(user.id));
  }

  function handleOpenGroupCover(group) {
    if (!canOpenGroupCover(group)) return;

    setFullCoverGroupId(group.id);
    setGroupCoverUpdateError("");
  }

  function handleGroupFilterWayOfFishingChange(nextWayOfFishing) {
    setGroupFilterWayOfFishing(nextWayOfFishing);
    setGroupFilterTypeOfFishing("all");
  }

  function handleGroupMetadataEditWayOfFishingChange(nextWayOfFishing) {
    setGroupMetadataEditWayOfFishing(nextWayOfFishing);
    setGroupMetadataEditTypeOfFishing("all");
  }

  function openGroupMetadataEditor(group) {
    setGroupMetadataEditTargetId(group.id);
    setGroupMetadataEditWaterType(getEditableGroupMetadataValue(group.waterType));
    setGroupMetadataEditWayOfFishing(
      getEditableGroupMetadataValue(group.wayOfFishing)
    );
    setGroupMetadataEditTypeOfFishing(
      getEditableGroupMetadataValue(group.typeOfFishing)
    );
    setGroupMetadataEditRules(group.groupRules || "");
    setGroupMetadataEditPostingApprovalMode(
      group.postingApprovalMode || "member_posts_auto_publish"
    );
    setGroupMetadataEditError("");
  }

  function closeGroupMetadataEditor(force = false) {
    if (groupMetadataEditLoading && !force) return;
    resetGroupMetadataEditState();
  }

  function handleGroupCoverUpdateChange(event) {
    const nextFile = event.target.files?.[0] || null;
    if (!nextFile || !fullCoverGroupId) return;

    setGroupCoverUpdateTargetId(fullCoverGroupId);
    setGroupCoverUpdateFile(nextFile);
    setGroupCoverUpdatePosition({ x: 50, y: 50 });
    setGroupCoverUpdateError("");

    if (groupCoverUpdateInputRef.current) {
      groupCoverUpdateInputRef.current.value = "";
    }
  }

  async function handleSaveUpdatedGroupCover() {
    if (!user || !groupCoverUpdateTargetId || !groupCoverUpdateFile) {
      return;
    }

    setGroupCoverUpdateLoading(true);
    setGroupCoverUpdateError("");

    try {
      const formData = new FormData();
      formData.append("coverImage", groupCoverUpdateFile);
      formData.append("coverPositionX", groupCoverUpdatePosition.x.toString());
      formData.append("coverPositionY", groupCoverUpdatePosition.y.toString());
      formData.append("coverScale", groupCoverUpdatePosition.scale.toString());

      const response = await fetch(
        `${API_BASE}/groups/${groupCoverUpdateTargetId}/cover`,
        withApiCredentials({
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(readErrorDetail(data, t("groups_cover_update_failed")));
      }

      upsertGroup(data, user.id);
      resetGroupCoverUpdateState();
      window.dispatchEvent(new Event("groupsUpdated"));
    } catch (err) {
      setGroupCoverUpdateError(err.message || t("groups_cover_update_failed"));
    } finally {
      setGroupCoverUpdateLoading(false);
    }
  }

  async function handleSaveGroupMetadata(event) {
    event.preventDefault();

    if (!user || !groupMetadataEditTargetId) {
      return;
    }

    setGroupMetadataEditLoading(true);
    setGroupMetadataEditError("");

    try {
      const response = await fetch(
        `${API_BASE}/groups/${groupMetadataEditTargetId}/metadata`,
        withApiCredentials({
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            waterType: groupMetadataEditWaterType,
            wayOfFishing: groupMetadataEditWayOfFishing,
            typeOfFishing: groupMetadataEditTypeOfFishing,
            groupRules: groupMetadataEditRules.trim(),
            postingApprovalMode: groupMetadataEditPostingApprovalMode,
          }),
        })
      );

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          readErrorDetail(
            data,
            t("groups_update_failed", {
              defaultValue: "Could not update group details.",
            })
          )
        );
      }

      upsertGroup(data, user.id);
      resetGroupMetadataEditState();
      window.dispatchEvent(new Event("groupsUpdated"));
    } catch (err) {
      setGroupMetadataEditError(
        err.message ||
          t("groups_update_failed", {
            defaultValue: "Could not update group details.",
          })
      );
    } finally {
      setGroupMetadataEditLoading(false);
    }
  }

  return {
    user,
    loading,
    error,
    groups,
    allUsers,
    ...createFormState,
    openCreateForm,
    closeCreateForm,
    cancelCreateGroup,
    groupFilterScope,
    setGroupFilterScope,
    groupFilterWaterType,
    setGroupFilterWaterType,
    groupFilterWayOfFishing,
    groupFilterTypeOfFishing,
    setGroupFilterTypeOfFishing,
    groupFilterTypeOfFishingOptions,
    groupMetadataEditTypeOfFishingOptions,
    ...membershipAdminController,
    activeGroupHomeId,
    fullCoverGroupId,
    groupCoverUpdatePreviewUrl,
    groupCoverUpdatePosition,
    setGroupCoverUpdatePosition,
    groupCoverUpdateLoading,
    groupCoverUpdateError,
    groupCoverUpdateInputRef,
    groupMetadataEditTargetId,
    groupMetadataEditWaterType,
    setGroupMetadataEditWaterType,
    groupMetadataEditWayOfFishing,
    groupMetadataEditTypeOfFishing,
    setGroupMetadataEditTypeOfFishing,
    groupMetadataEditRules,
    setGroupMetadataEditRules,
    groupMetadataEditPostingApprovalMode,
    setGroupMetadataEditPostingApprovalMode,
    groupMetadataEditLoading,
    groupMetadataEditError,
    ...groupPostsController,
    selectedGroup,
    activeGroupHome,
    activeGroupRequiresPostApproval,
    fullCoverGroup,
    groupCoverUpdateTarget,
    groupMetadataEditTarget,
    resetGroupCoverUpdateState,
    closeGroupCoverViewer,
    openGroupCoverUpdatePicker,
    openGroup,
    closeGroupHome,
    openGroupHome,
    closeGroupDetail,
    filteredGroups,
    getUserById,
    handleOpenMemberProfile,
    canOpenGroupCover,
    handleOpenGroupCover,
    handleGroupFilterWayOfFishingChange,
    handleGroupMetadataEditWayOfFishingChange,
    openGroupMetadataEditor,
    closeGroupMetadataEditor,
    handleGroupCoverUpdateChange,
    handleSaveUpdatedGroupCover,
    handleSaveGroupMetadata,
  };
}
